import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from '@simplewebauthn/browser';
import type { DeviceInfo } from '../../shared/types';
import { apiGet, apiPost } from './api';

/** App-constant PRF input; the per-envelope salt lives in the wrapper, so this needs no secrecy. */
const PRF_SALT = new TextEncoder().encode('canary-wallet-kek-v1');

/** lib.dom has no PRF types yet; this is the subset we touch. */
type PrfExt = { prf?: { enabled?: boolean; results?: { first?: ArrayBuffer } } };

export async function loginWithPasskey(): Promise<void> {
  const { challengeId, options } = await apiPost<{
    challengeId: string;
    options: PublicKeyCredentialRequestOptionsJSON;
  }>('/api/auth/challenge', { mode: 'login' });
  const credential: AuthenticationResponseJSON = await startAuthentication({ optionsJSON: options });
  await apiPost('/api/auth/verify', { challengeId, credential });
}

/**
 * Login ceremony that also evaluates the PRF extension. The 32-byte output is
 * the seed for this passkey's vault key and never leaves the browser: it is
 * stripped from the response before the assertion is sent for verification.
 */
export async function authenticateWithPrf(): Promise<{ credId: string; prf: ArrayBuffer }> {
  const { challengeId, options } = await apiPost<{
    challengeId: string;
    options: PublicKeyCredentialRequestOptionsJSON;
  }>('/api/auth/challenge', { mode: 'login' });
  (options as unknown as { extensions: unknown }).extensions = { prf: { eval: { first: PRF_SALT } } };
  const credential = await startAuthentication({ optionsJSON: options });
  const ext = credential.clientExtensionResults as PrfExt;
  const prf = ext.prf?.results?.first;
  delete ext.prf;
  await apiPost('/api/auth/verify', { challengeId, credential });
  if (!prf) {
    throw new Error('This passkey did not return a PRF secret. Use a platform passkey (Face ID, Touch ID, Windows Hello) or a passphrase.');
  }
  return { credId: credential.id, prf };
}

export async function registerPasskey(bootstrapToken?: string): Promise<{ deviceName: string }> {
  const { challengeId, options } = await apiPost<{
    challengeId: string;
    options: PublicKeyCredentialCreationOptionsJSON;
  }>('/api/auth/challenge', { mode: 'register' });
  const credential: RegistrationResponseJSON = await startRegistration({ optionsJSON: options });
  const ext = credential.clientExtensionResults as PrfExt;
  const prfEnabled = ext.prf?.enabled === true;
  delete ext.prf;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (bootstrapToken) headers['x-bootstrap-token'] = bootstrapToken;
  const r = await fetch('/api/auth/register', {
    method: 'POST',
    credentials: 'same-origin',
    headers,
    body: JSON.stringify({ challengeId, credential, prfEnabled }),
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json() as Promise<{ deviceName: string }>;
}

export type AuthStatus = { hasDevices: boolean; devBypass: boolean };

export async function getAuthStatus(): Promise<AuthStatus> {
  return apiGet<AuthStatus>('/api/auth/register-status');
}

export async function listDevices(): Promise<DeviceInfo[]> {
  return (await apiGet<{ devices: DeviceInfo[] }>('/api/auth/devices')).devices;
}
